import { useMemo, useState, useRef } from 'react';
import { useIntl } from 'react-intl';

import { useAppDispatch } from '@verantos-dev/app-state-base';
import { store } from '@verantos-dev/app-state-base';
import {
  queueEventModalsForConceptGroups,
  selectQueuedEventModalSelections,
  selectConceptGroupSelectionsByFilterKey,
  selectEventIdFromContainerName,
  getAutoAcceptConceptGroup,
} from '@verantos-dev/app-state-cohort-builder';
import { IsValidCreateUploadResponse } from '@verantos-dev/types-concepts';

import { useCreateConceptGroup } from './useCreateConceptGroup';
import { useValidateFile } from './useValidateFile';
import { useValidateName } from './useValidateName';
import { NameValidationStatus } from '../components/ConceptGroupNameInput';
import { createConceptsFile } from '../utils/conceptCsvUtils';

interface InitialData {
  conceptGroupName: string;
  concepts: IsValidCreateUploadResponse[];
  filterKey?: string; // Filter key for tracking concept group selection
  // Preview fields (present when concept_group_preview_mode is enabled)
  previewId?: string;
  totalCount?: number;
  domainBreakdown?: Record<string, number>;
}

// Behavior hook for form state management
export const useConceptGroupFormState = (initialData?: InitialData) => {
  const [conceptGroupName, setConceptGroupName] = useState(initialData?.conceptGroupName || '');

  // Use lazy initializer to create file synchronously during initialization
  // This avoids flickering from useEffect state updates while ensuring fresh file on each mount
  // The key prop forces remounting, so this function runs fresh for each dialog instance
  // Skip CSV file creation for preview mode — concepts are fetched on demand via REST
  const [selectedFile, setSelectedFile] = useState<File | null>(() => {
    if (initialData?.previewId) {
      return null; // Preview mode: no file needed
    }
    if (initialData?.concepts && initialData.concepts.length > 0) {
      const timestamp = new Date().toISOString().replace(/[-:T]/g, '').slice(0, 13);
      const baseName = initialData.conceptGroupName
        ? initialData.conceptGroupName.replace(/\s+/g, '_').toLowerCase()
        : 'initial-concepts';
      const filename = `${baseName}_${timestamp}.csv`;
      return createConceptsFile(initialData.concepts, filename);
    }
    return null;
  });

  return {
    conceptGroupName,
    setConceptGroupName,
    selectedFile,
    setSelectedFile,
  };
};

// Behavior hook for validation logic
export const useConceptGroupValidation = (
  conceptGroupName: string,
  selectedFile: File | null,
  projectId?: string
) => {
  const { nameValidationError, nameValidationStatus } = useValidateName(
    conceptGroupName,
    projectId
  );
  const {
    validate,
    concepts: fileConcepts,
    warnings: fileWarnings,
    isValidating,
    validationError,
  } = useValidateFile(selectedFile);

  return {
    nameValidationError,
    nameValidationStatus,
    validate,
    fileConcepts,
    fileWarnings,
    isValidating,
    validationError,
  };
};

// Behavior hook for workflow step navigation
export const useConceptGroupWorkflow = (initialData?: InitialData) => {
  const [activeStep, setActiveStep] = useState(initialData ? 1 : 0);
  const intl = useIntl();

  const steps = [
    intl.formatMessage({ id: 'conceptGroup.steps.upload' }),
    intl.formatMessage({ id: 'conceptGroup.steps.review' }),
  ];

  const onNext = async (validate: () => Promise<void>, create: () => Promise<unknown>) => {
    if (activeStep === 0) {
      try {
        await validate();
        setActiveStep(1);
      } catch (e) {
        console.error('Failed to upload file', e);
      }
    } else if (activeStep === 1) {
      try {
        await create();
      } catch (e) {
        console.error('Failed to create concept group', e);
      }
    }
  };

  const onBack = () => {
    if (activeStep === 1) {
      setActiveStep(0);
    }
  };

  return {
    activeStep,
    steps,
    onNext,
    onBack,
  };
};

// Behavior hook for concept group creation
export const useConceptGroupCreation = (
  conceptGroupName: string,
  concepts: IsValidCreateUploadResponse[],
  projectId?: string,
  onSuccessCallback?: (conceptGroupId: string) => void,
  filterKey?: string,
  previewId?: string
) => {
  // Store the created concept group ID and related data for later use when dialog closes
  const createdConceptGroupDataRef = useRef<{
    conceptGroupId: string;
    conceptGroupName: string;
    filterKey?: string;
  } | null>(null);

  // Create a custom success callback that stores data for later use
  const handleConceptGroupCreated = useMemo(() => {
    return (conceptGroupId: string, returnedFilterKey?: string) => {
      // Call the original callback if provided
      if (onSuccessCallback) {
        onSuccessCallback(conceptGroupId);
      }

      // Store the concept group data for later use when dialog closes
      createdConceptGroupDataRef.current = {
        conceptGroupId,
        conceptGroupName,
        filterKey: returnedFilterKey || filterKey, // Use returned filter_key or fallback to passed filter_key
      };
    };
  }, [onSuccessCallback, conceptGroupName, filterKey]);

  const { create, isSuccess, isCreating, creationError } = useCreateConceptGroup(
    conceptGroupName,
    concepts,
    projectId,
    handleConceptGroupCreated,
    filterKey,
    previewId
  );

  return {
    create,
    isSuccess,
    isCreating,
    creationError,
    createdConceptGroupDataRef,
  };
};

// Behavior hook for event modal handling
export const useConceptGroupEventHandling = (
  createdConceptGroupDataRef: React.MutableRefObject<{
    conceptGroupId: string;
    conceptGroupName: string;
    filterKey?: string;
  } | null>
) => {
  const dispatch = useAppDispatch();

  const handleDialogClose = () => {
    if (createdConceptGroupDataRef.current) {
      const {
        conceptGroupId,
        conceptGroupName: createdConceptGroupName,
        filterKey,
      } = createdConceptGroupDataRef.current;

      // Check for registered auto-accept callback (AV flag on, set by CohortVersionLayout)
      const autoAccept = getAutoAcceptConceptGroup();
      if (autoAccept && filterKey) {
        autoAccept({ conceptGroupId, conceptGroupName: createdConceptGroupName, filterKey });
        createdConceptGroupDataRef.current = null;
        return;
      }

      // Use filter_key for precise matching if available
      if (filterKey) {
        const state = store.getState();
        const selector = selectConceptGroupSelectionsByFilterKey(filterKey);
        const selection = selector(state);

        if (selection) {
          // Find event ID from container name
          const eventIdSelector = selectEventIdFromContainerName(selection.containerName);
          const eventId = eventIdSelector(state);

          if (eventId) {
            // Queue single event modal (event-level queueing)
            dispatch(
              queueEventModalsForConceptGroups({
                selections: [
                  {
                    eventId,
                    filterKey: selection.filterKey,
                    filterType: selection.filterType,
                    attribute: selection.attribute,
                  },
                ],
                conceptGroupId,
              })
            );
          }
        }
      } else {
        // Fallback to span name matching
        const state = store.getState();
        const selector = selectQueuedEventModalSelections(createdConceptGroupName);
        const queuedEventModalSelections = selector(state);

        // Use selector result directly (selector handles filtering, mapping, transformation, deduplication)
        if (queuedEventModalSelections.length > 0) {
          dispatch(
            queueEventModalsForConceptGroups({
              selections: queuedEventModalSelections,
              conceptGroupId,
            })
          );
        }
      }

      // Clear the stored data
      createdConceptGroupDataRef.current = null;
    }
  };

  return {
    handleDialogClose,
  };
};

// Main composed hook
export const useConceptGroupWorkflowDialog = (
  projectId?: string,
  onSuccessCallback?: (conceptGroupId: string) => void,
  initialData?: InitialData
) => {
  // Form state behavior - always available
  const { conceptGroupName, setConceptGroupName, selectedFile, setSelectedFile } =
    useConceptGroupFormState(initialData);

  // Validation behavior - always available
  const {
    nameValidationError,
    nameValidationStatus,
    validate,
    fileConcepts,
    fileWarnings,
    isValidating,
    validationError,
  } = useConceptGroupValidation(conceptGroupName, selectedFile, projectId);

  // Workflow behavior - always available
  const { activeStep, steps, onNext, onBack } = useConceptGroupWorkflow(initialData);

  // Use file concepts if a file has been uploaded and validated, otherwise use initial concepts
  // This ensures that when a user uploads a modified file, it takes precedence over initialData
  const concepts =
    fileConcepts && fileConcepts.length > 0 ? fileConcepts : initialData?.concepts || [];

  // Creation behavior - always available
  const { create, isSuccess, isCreating, creationError, createdConceptGroupDataRef } =
    useConceptGroupCreation(
      conceptGroupName,
      concepts || [],
      projectId,
      onSuccessCallback,
      initialData?.filterKey,
      initialData?.previewId
    );

  // Event handling behavior for event modal queuing for generated concept groups with warnings/errors
  const { handleDialogClose } = useConceptGroupEventHandling(createdConceptGroupDataRef);

  // Validation for submit button
  const isValidForSubmit = useMemo(() => {
    // Common validation checks for both steps
    const isNameInvalid = [
      NameValidationStatus.Error,
      NameValidationStatus.Pending,
      NameValidationStatus.Invalid,
    ].includes(nameValidationStatus as NameValidationStatus);

    const isNameEmpty = !conceptGroupName.trim();

    if (isNameInvalid || isNameEmpty) return false;

    if (activeStep === 0) {
      if (!selectedFile) return false;
    }

    if (activeStep === 1) {
      // Preview mode: always valid for submit (confirmation uses server-side data)
      if (initialData?.previewId) return true;
      // Legacy mode: check concepts validity
      if (concepts && concepts.every((c) => !c.isValid)) return false;
    }

    return true;
  }, [
    conceptGroupName,
    concepts,
    nameValidationStatus,
    selectedFile,
    activeStep,
    initialData?.previewId,
  ]);

  // Compose the onNext handler with validation and creation functions
  const handleNext = () => onNext(validate, create);

  return {
    // Form state - always available
    conceptGroupName,
    onConceptGroupNameChange: setConceptGroupName,
    selectedFile,
    onFileSelect: setSelectedFile,

    // Validation state - always available
    nameValidationStatus,
    nameValidationError,

    // Workflow state - always available
    activeStep,
    steps,
    isValidForSubmit,
    concepts,
    fileWarnings,
    isReviewMode: Boolean(initialData),

    // Preview state (present when concept group was RA-generated with preview)
    previewId: initialData?.previewId,
    totalCount: initialData?.totalCount,
    domainBreakdown: initialData?.domainBreakdown,

    // Loading and error states - always available
    isLoading: isValidating || isCreating,
    validationError,
    creationError,
    isSuccess,

    // Actions - always available
    onNext: handleNext,
    onBack,
    handleDialogClose,
  };
};
